import React, {useState, useEffect, useMemo} from "react";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Badge from "@mui/material/Badge";

import {visuallyHidden} from "@mui/utils";
import {ReportsServices, FilterDataServices, CommentsServices, AddressServices, ContractorServices} from "../../services/reportsServices";
import CustomPagination from "../../CustomComponents/CustomPagination";
import {AutoCompleteSelectMultiple, TextFieldForm} from "../../CustomComponents/inputs";
import Rating from "@mui/material/Rating";
import moment from "moment";

import CustomUserName from "../../CustomComponents/CustomUserName";
import CustomRatingStar from "../../CustomComponents/CustomRatingStar";

import {Form, Formik} from "formik";

import * as Yup from "yup";

import Stack from "@mui/material/Stack";
import {getStatusBadge, getTypeCommentsBadge, truncateWords} from "../../utils/format-text";
import CustomLoader from "../../CustomComponents/CustomLoader";
import {fDate} from "../../utils/format-time";

const Reports = () => {
	const [reports, setReports] = useState("ratings");
	const [sortBy, setSortBy] = useState("date-desc");
	const [open, setOpen] = useState(false);

	const [addressesList, setAddressesList] = useState([]);
	const [userList, setUserList] = useState([]);
	const [filterObject, setFilterObject] = useState({});
	const [filterData, setFilterData] = useState({});
	const [downloading, setDownloading] = useState(false);

	const mergedFilter = useMemo(() => ({...filterObject, sortBy}), [filterObject, sortBy]);

	const getUserFullName = (user) => {
		if (!user) return "-";
		if (user.full_name) return user.full_name;
		const first = user.first_name || user.firstname || "";
		const last = user.last_name || user.lastname || "";
		const name = `${first} ${last}`.trim();
		return name || user.username || user.email || "-";
	};

	const csvEscape = (value) => {
		if (value === null || value === undefined) return "";
		const str = String(value).replace(/\r?\n|\r/g, " ");
		if (/[",\n]/.test(str)) {
			return '"' + str.replace(/"/g, '""') + '"';
		}
		return str;
	};

	const buildAndDownloadCsv = (headers, rows, filename) => {
		const headerLine = headers.map(csvEscape).join(",");
		const dataLines = rows.map((r) => r.map(csvEscape).join(","));
		const csvContent = [headerLine, ...dataLines].join("\n");
		const blob = new Blob(["\ufeff" + csvContent], {type: "text/csv;charset=utf-8;"});
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", filename);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	const handleDownloadCsv = async () => {
		try {
			setDownloading(true);
			const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
			let serviceFn = null;
			let headers = [];
			let mapRow = null;

			if (reports === "ratings") {
				serviceFn = ReportsServices;
				headers = ["Address", "User Name", "Rating", "Job Date", "Status", "Submitted"];
				mapRow = (row) => {
					const status = row?.admin_flagged ? "Flagged" : row?.is_historic ? "Historic" : "Fresh";
					return [
						row?.address?.full_address || "-",
						getUserFullName(row?.user),
						row?.stars ?? "-",
						row?.job_date ? (fDate(row?.job_date) || "-") : "-",
						status,
						row?.createdAt ? (fDate(row?.createdAt) || "-") : "-",
					];
				};
			} else if (reports === "comments") {
				serviceFn = CommentsServices;
				headers = ["Street/Zipcode", "User Name", "Type", "Job Date", "Status", "Submitted"];
				mapRow = (row) => {
					const status = row?.admin_flagged ? "Flagged" : row?.is_historic ? "Historic" : "Fresh";
					const type = row?.comment_type === "street" ? "Street" : row?.comment_type === "zipcode" ? "Zipcode" : (row?.comment_type || "-");
					return [
						row?.addresses || "-",
						getUserFullName(row?.user),
						type,
						row?.comment_date ? (fDate(row?.comment_date) || "-") : "-",
						status,
						row?.createdAt ? (fDate(row?.createdAt) || "-") : "-",
					];
				};
			} else if (reports === "address") {
				serviceFn = AddressServices;
				headers = ["Address", "User Name", "Rating", "Type", "Submitted"];
				mapRow = (row) => {
					const type = row?.manual_id == 1 ? "Manually" : "Google";
					return [
						row?.full_address || "-",
						getUserFullName(row?.user),
						row?.aggregate_rating ?? "-",
						type,
						row?.createdAt ? (fDate(row?.createdAt) || "-") : "-",
					];
				};
			} else if (reports === "contractor") {
				serviceFn = ContractorServices;
				headers = ["Contractor Name", "Contractor Email", "Business", "Verified", "Created"];
				mapRow = (row) => {
					const verified = row?.verified == 1 ? "Verified" : "Not Verified";
					return [
						getUserFullName(row),
						row?.email || "-",
						row?.business_name || "-",
						verified,
						row?.createdAt ? (moment(row?.createdAt)?.format("MMM DD, YYYY") || "-") : "-",
					];
				};
			}

			if (!serviceFn || !mapRow) return;

			// Step 1: fetch total count with minimal payload
			const initialRes = await serviceFn({page: 1, pageSize: 1, ...mergedFilter});
			const total = initialRes?.data?.pagination?.total || 0;
			if (!total) {
				buildAndDownloadCsv(headers, [], `${reports}-reports-${timestamp}.csv`);
				return;
			}

			// Step 2: fetch all rows based on total
			const allRes = await serviceFn({page: 1, pageSize: total, ...mergedFilter});
			const list = allRes?.data?.list || [];
			const rows = list.map(mapRow);
			buildAndDownloadCsv(headers, rows, `${reports}-reports-${timestamp}.csv`);
		} catch (e) {
			// noop for now
		} finally {
			setDownloading(false);
		}
	};

	const hasValue = (value) => {
		if (value === null || value === undefined) return false;
		if (Array.isArray(value)) return value.length > 0;
		if (typeof value === "string") return value.trim() !== "";
		if (typeof value === "object") return Object.keys(value).length > 0;
		return true;
	};

	const count = Object.values(filterObject)?.filter(hasValue).length;

	useEffect(() => {
		FilterDataServices()
			.then((res) => {
				if (res?.status) {
					setFilterData(res?.data);
					setAddressesList(res?.data?.addresses);
					setUserList(res?.data?.user);
				}
			})
			.catch(() => {});
	}, []);

	return (
		<div className="admin-ratings-dashboard-page">
			<div className="page-header">
				<h2>Reports</h2>
			</div>

			<div className="ratings-controls  flex-row justify-content-between">
				<div className="filter-section">
					<select
						className="filter-select"
						value={reports}
						onChange={(e) => {
							setFilterObject({});
							setReports(e.target.value);
						}}>
						<option value="ratings">Ratings Reports</option>
						<option value="comments">Comments Reports</option>
						<option value="address">Address Reports</option>
						<option value="contractor">Contractor Reports</option>
					</select>

					<select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="date-desc">Newest First</option>
						<option value="date-asc">Oldest First</option>
					</select>
				</div>

				<div className="filter-section">
					 <Button variant="contained" className="btn-primary" size="small" onClick={handleDownloadCsv} disabled={downloading}>
						<svg xmlns="http://www.w3.org/2000/svg" width="18px" height="18px" viewBox="0 0 448 512">
							<rect width="448" height="512" fill="none" />
							<path
								fill="currentColor"
								d="M256 32c0-17.7-14.3-32-32-32s-32 14.3-32 32v210.7l-41.4-41.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l96 96c12.5 12.5 32.8 12.5 45.3 0l96-96c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 242.7zM64 320c-35.3 0-64 28.7-64 64v32c0 35.3 28.7 64 64 64h320c35.3 0 64-28.7 64-64v-32c0-35.3-28.7-64-64-64h-46.9l-56.6 56.6c-31.2 31.2-81.9 31.2-113.1 0L110.9 320zm304 56a24 24 0 1 1 0 48a24 24 0 1 1 0-48"
							/>
						</svg>
					</Button>
					<Badge badgeContent={count > 0 ? count : null} color="error">
						<Button
							onClick={() => {
								setOpen(true);
							}}
							variant="outlined"
							size="small"
							startIcon={
								<svg xmlns="http://www.w3.org/2000/svg" width="15px" height="15px" viewBox="0 0 24 24">
									<rect width="24" height="24" fill="none" />
									<path
										fill="currentColor"
										stroke="currentColor"
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M4 3h16a1 1 0 0 1 1 1v1.586a1 1 0 0 1-.293.707l-6.414 6.414a1 1 0 0 0-.293.707v6.305a1 1 0 0 1-1.242.97l-2-.5a1 1 0 0 1-.758-.97v-5.805a1 1 0 0 0-.293-.707L3.293 6.293A1 1 0 0 1 3 5.586V4a1 1 0 0 1 1-1"
									/>
								</svg>
							}>
							Filter
						</Button>
					</Badge>
				</div>

				<Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
					<Formik
						enableReinitialize
						initialValues={filterObject}
						validationSchema={Yup.object().shape({})}
						onSubmit={(values) => {
							setFilterObject(values);
							setOpen(false);
						}}>
						{(props) => {
							const {setFieldValue, handleSubmit, resetForm, dirty, values} = props;
							return (
								<Form noValidate style={{height: "100%"}}>
									<Box sx={{display: "flex", flexDirection: "column", height: "100%", minWidth: 500, maxWidth: 500}}>
										<Box sx={{background: "#28a745", color: "#FFF", fontSize: 20, fontWeight: 600, p: 2}}>Filter Reports</Box>

										<Box sx={{flex: 1, overflowY: "auto", p: 3}}>
											{reports === "ratings" && (
												<Stack spacing={2}>
													<Box>
														<Typography variant="body2" fontWeight={700}>
															Rating
														</Typography>

														<Rating
															value={values.rating}
															onChange={(e) => {
																setFieldValue("rating", e.target.value);
															}}
															size="large"
															sx={{fontSize: 30}}
														/>
													</Box>

													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Address"
														field="address"
														menuList={filterData?.rating?.addresses}
														valueKey="addresses_id"
														labelKey="full_address"
														limitTags={5}
													/>

													<AutoCompleteSelectMultiple required={false} formik={props} label="User Name" field="user" menuList={filterData?.rating?.users} valueKey="user_id" labelKey="full_name" limitTags={5} />

													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Status"
														field="status"
														menuList={[
															{key: "fresh", value: "Fresh"},
															{key: "historic", value: "Historic"},
															{key: "flagged", value: "Flagged"},
														]}
														valueKey="key"
														labelKey="value"
														limitTags={5}
													/>

													<Stack direction="row" spacing={2}>
														<TextFieldForm required={false} type="date" formik={props} field="job_date_from" label="Job Date From" />
														<TextFieldForm required={false} type="date" formik={props} field="job_date_to" label="Job Date To" />
													</Stack>
												</Stack>
											)}

											{reports === "comments" && (
												<Stack spacing={2}>
													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Street/Zipcode"
														field="streetzipcode"
														menuList={filterData?.comment?.addresses}
														valueKey="full_address"
														labelKey="full_address"
														limitTags={5}
													/>

													<AutoCompleteSelectMultiple required={false} formik={props} label="User Name" field="user" menuList={filterData?.comment?.users} valueKey="user_id" labelKey="full_name" limitTags={5} />

													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Type"
														field="type"
														menuList={[
															{key: "street", value: "Street"},
															{key: "zipcode", value: "Zipcode"},
														]}
														valueKey="key"
														labelKey="value"
														limitTags={5}
													/>

													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Status"
														field="status"
														menuList={[
															{key: "fresh", value: "Fresh"},
															{key: "historic", value: "Historic"},
															{key: "flagged", value: "Flagged"},
														]}
														valueKey="key"
														labelKey="value"
														limitTags={5}
													/>

													<Stack direction="row" spacing={2}>
														<TextFieldForm required={false} type="date" formik={props} field="job_date_from" label="Job Date From" />
														<TextFieldForm required={false} type="date" formik={props} field="job_date_to" label="Job Date To" />
													</Stack>
												</Stack>
											)}

											{reports === "address" && (
												<Stack spacing={2}>
													<Box>
														<Typography variant="body2" fontWeight={700}>
															Rating
														</Typography>

														<Rating
															value={values.rating}
															onChange={(e) => {
																setFieldValue("rating", e.target.value);
															}}
															size="large"
															sx={{fontSize: 30}}
														/>
													</Box>

													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Address"
														field="address"
														menuList={filterData?.addressesObj?.addresses}
														valueKey="addresses_id"
														labelKey="full_address"
														limitTags={5}
													/>

													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="User Name"
														field="user"
														menuList={filterData?.addressesObj?.users}
														valueKey="user_id"
														labelKey="full_name"
														limitTags={5}
													/>

													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Type"
														field="type"
														menuList={[
															{key: "manually", value: "Manually"},
															{key: "google", value: "Google"},
														]}
														valueKey="key"
														labelKey="value"
														limitTags={5}
													/>

													<Stack direction="row" spacing={2}>
														<TextFieldForm required={false} type="date" formik={props} field="submitted_from" label="Submitted From" />
														<TextFieldForm required={false} type="date" formik={props} field="submitted_to" label="Submitted To" />
													</Stack>
												</Stack>
											)}

											{reports === "contractor" && (
												<Stack spacing={2}>
													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Contractor Name"
														field="names"
														menuList={filterData?.contractor?.contractorName}
														valueKey="id"
														labelKey="value"
														limitTags={5}
													/>

													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Contractor Email"
														field="emails"
														menuList={filterData?.contractor?.contractorEmail}
														valueKey="id"
														labelKey="value"
														limitTags={5}
													/>
													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Business"
														field="business"
														menuList={filterData?.contractor?.business}
														valueKey="value"
														labelKey="value"
														limitTags={5}
													/>

													<AutoCompleteSelectMultiple
														required={false}
														formik={props}
														label="Verified"
														field="verified"
														menuList={[
															{key: "yes", value: "Verified"},
															{key: "no", value: "Not Verified"},
														]}
														valueKey="key"
														labelKey="value"
														limitTags={5}
													/>

													<Stack direction="row" spacing={2}>
														<TextFieldForm required={false} type="date" formik={props} field="created_from" label="Created From" />
														<TextFieldForm required={false} type="date" formik={props} field="created_to" label="Created To" />
													</Stack>
												</Stack>
											)}
										</Box>

										<Box sx={{p: 2, borderTop: "1px solid #ddd", display: "flex"}}>
											<Button
												fullWidth
												variant="outlined"
												sx={{mr: 1}}
												onClick={() => {
													resetForm();
													setFilterObject({});
													setOpen(false);
												}}
												color="CancelButton">
												Clear
											</Button>

											<Button fullWidth variant="contained" type="submit" form="your-form-id" disabled={!dirty} onClick={handleSubmit}>
												Apply
											</Button>
										</Box>
									</Box>
								</Form>
							);
						}}
					</Formik>
				</Drawer>
			</div>

			<div className="card">
				{reports === "ratings" && <RatingsReports ratingsFilter={mergedFilter} />}
				{reports === "comments" && <CommentsReports commentsFilter={mergedFilter} />}
				{reports === "address" && <AddressReports addressFilter={mergedFilter} />}
				{reports === "contractor" && <ContractorReports contractorFilter={mergedFilter} />}
			</div>
		</div>
	);
};

const RatingsReports = ({ratingsFilter}) => {
	const [order, setOrder] = useState("asc");
	const [orderBy, setOrderBy] = useState("calories");
	const [list, setList] = useState([]);

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalRecode, setTotalRecode] = useState(0);
	const [loading, setLoading] = useState(true);

	const headCells = [
		{id: "address", label: "Address", width: "25%"},
		{id: "username", label: "User Name", width: "20%"},
		{id: "rating", label: "Rating", width: "13.5%"},
		{id: "job_date", label: "Job Date", width: "13.5%"},
		{id: "status", label: "Status", width: "13.5%"},
		{id: "submitted", label: "Submitted", width: "13.5%"},
	];

	function descendingComparator(a, b, orderBy) {
		if (b[orderBy] < a[orderBy]) return -1;
		if (b[orderBy] > a[orderBy]) return 1;
		return 0;
	}

	function getComparator(order, orderBy) {
		return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
	}

	function stableSort(array, comparator) {
		const stabilized = array.map((el, index) => [el, index]);
		stabilized.sort((a, b) => {
			const order = comparator(a[0], b[0]);
			if (order !== 0) return order;
			return a[1] - b[1];
		});
		return stabilized.map((el) => el[0]);
	}

	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	useEffect(() => {
		setLoading(true);
		ReportsServices({page, pageSize, order, orderBy, ...ratingsFilter})
			.then((res) => {
				setLoading(false);
				if (res?.status) {
					setList(res?.data?.list || []);
					setTotalRecode(res?.data?.pagination?.total || 0);
				}
			})
			.catch(() => {
				setList([]);
				setTotalRecode(0);
			});
	}, [page, pageSize, order, orderBy, ratingsFilter]);

	useEffect(() => {
		setPage(1);
	}, [ratingsFilter]);

	return (
		<Paper sx={{width: "100%", overflow: "hidden"}}>
			<TableContainer>
				<Table aria-labelledby="ratings table" sx={{minWidth: 750}}>
					{/* Table Head */}
					<TableHead>
						<TableRow>
							{headCells.map((headCell) => (
								<TableCell key={headCell.id} sx={{fontWeight: 600, width: headCell.width}} sortDirection={orderBy === headCell.id ? order : false}>
									{headCell.label}
									{/* <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : "asc"} onClick={() => handleRequestSort(headCell.id)}>
										{orderBy === headCell.id ? (
											<Box component="span" sx={visuallyHidden}>
												{order === "desc" ? "sorted descending" : "sorted ascending"}
											</Box>
										) : null}
									</TableSortLabel> */}
								</TableCell>
							))}
						</TableRow>
					</TableHead>

					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={headCells.length}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											height: "400px",
										}}>
										<CustomLoader />
									</Box>
								</TableCell>
							</TableRow>
						) : (
							<>
								{stableSort(list, getComparator(order, orderBy)).map((row, index) => (
									<TableRow hover key={index} sx={{cursor: "pointer"}}>
										<TableCell>{truncateWords(row?.address?.full_address, 30) || "-"}</TableCell>
										<TableCell>
											<CustomUserName user={row?.user} />
										</TableCell>
										<TableCell sx={{alignItems: "center"}}>
											<CustomRatingStar star={row?.stars} />
										</TableCell>

										<TableCell>{fDate(row?.job_date)}</TableCell>
										<TableCell>{getStatusBadge(row.is_historic, row?.admin_flagged)}</TableCell>
										<TableCell>{fDate(row?.createdAt)}</TableCell>
									</TableRow>
								))}

								{list.length === 0 && (
									<TableRow>
										<TableCell colSpan={headCells.length}>
											<Typography align="center" sx={{py: 2}}>
												No records found
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{!loading && (
				<CustomPagination
					current={page}
					pageSize={pageSize}
					total={totalRecode}
					onChange={(e) => setPage(e)}
					onShowSizeChange={(p, e) => {
						setPage(p);
						setPageSize(e);
					}}
				/>
			)}
		</Paper>
	);
};

const CommentsReports = ({commentsFilter}) => {
	const [order, setOrder] = useState("asc");
	const [orderBy, setOrderBy] = useState("calories");
	const [list, setList] = useState([]);

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalRecode, setTotalRecode] = useState(0);
	const [loading, setLoading] = useState(true);

	const headCells = [
		{id: "street", label: "Street/Zipcode", width: "25%"},
		{id: "username", label: "User Name", width: "20%"},
		{id: "type", label: " Type", width: "13.5%"},
		{id: "job_date", label: "Job Date", width: "13.5%"},
		{id: "Status", label: "Status", width: "13.5%"},
		{id: "submitted", label: "Submitted", width: "13.5%"},
	];

	function descendingComparator(a, b, orderBy) {
		if (b[orderBy] < a[orderBy]) return -1;
		if (b[orderBy] > a[orderBy]) return 1;
		return 0;
	}

	function getComparator(order, orderBy) {
		return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
	}

	function stableSort(array, comparator) {
		const stabilized = array.map((el, index) => [el, index]);
		stabilized.sort((a, b) => {
			const order = comparator(a[0], b[0]);
			if (order !== 0) return order;
			return a[1] - b[1];
		});
		return stabilized.map((el) => el[0]);
	}

	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	useEffect(() => {
		setLoading(true);
		CommentsServices({page, pageSize, order, orderBy, ...commentsFilter})
			.then((res) => {
				setLoading(false);
				if (res?.status) {
					setList(res?.data?.list || []);
					setTotalRecode(res?.data?.pagination?.total || 0);
				} else {
					setList([]);
					setTotalRecode(0);
				}
			})
			.catch(() => {
				setLoading(false);
				setList([]);
				setTotalRecode(0);
			});
	}, [page, pageSize, order, orderBy, commentsFilter]);

	useEffect(() => {
		setPage(1);
	}, [commentsFilter]);

	return (
		<Paper sx={{width: "100%", overflow: "hidden"}}>
			<TableContainer>
				<Table aria-labelledby="ratings table" sx={{minWidth: 750}}>
					{/* Table Head */}
					<TableHead>
						<TableRow>
							{headCells.map((headCell) => (
								<TableCell key={headCell.id} sx={{fontWeight: 600, width: headCell.width}} sortDirection={orderBy === headCell.id ? order : false}>
									{headCell.label}
									{/* <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : "asc"} onClick={() => handleRequestSort(headCell.id)}>
										{orderBy === headCell.id ? (
											<Box component="span" sx={visuallyHidden}>
												{order === "desc" ? "sorted descending" : "sorted ascending"}
											</Box>
										) : null}
									</TableSortLabel> */}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={headCells.length}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											height: "400px",
										}}>
										<CustomLoader />
									</Box>
								</TableCell>
							</TableRow>
						) : (
							<>
								{stableSort(list, getComparator(order, orderBy)).map((row, index) => (
									<TableRow hover key={index} sx={{cursor: "pointer"}}>
										<TableCell>{truncateWords(row?.addresses, 40) || "-"}</TableCell>
										<TableCell>
											<CustomUserName user={row?.user} />
										</TableCell>
										<TableCell>{getTypeCommentsBadge(row.comment_type)}</TableCell>
										<TableCell>{fDate(row?.comment_date)}</TableCell>
										<TableCell>{getStatusBadge(row.is_historic, row?.admin_flagged)}</TableCell>
										<TableCell>{fDate(row?.createdAt)} </TableCell>
									</TableRow>
								))}

								{list.length === 0 && (
									<TableRow>
										<TableCell colSpan={headCells.length}>
											<Typography align="center" sx={{py: 2}}>
												No records found
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{!loading && (
				<CustomPagination
					current={page}
					pageSize={pageSize}
					total={totalRecode}
					onChange={(e) => setPage(e)}
					onShowSizeChange={(p, e) => {
						setPage(p);
						setPageSize(e);
					}}
				/>
			)}
		</Paper>
	);
};

const AddressReports = ({addressFilter}) => {
	const [order, setOrder] = useState("asc");
	const [orderBy, setOrderBy] = useState("calories");
	const [list, setList] = useState([]);

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalRecode, setTotalRecode] = useState(0);
	const [loading, setLoading] = useState(true);

	const getStatusBadge = (status) => {
		if (status == 1) {
			return <Chip label="Manually" variant="" color="info" />;
		} else {
			return <Chip label="Google" variant="" color="success" />;
		}
	};

	const headCells = [
		{id: "address", label: "Address", width: "25%"},
		{id: "username", label: "User Name", width: "20%"},
		{id: "rating", label: "Rating", width: "13.5%"},
		{id: "status", label: "Type", width: "13.5%"},
		{id: "submitted", label: "Submitted", width: "13.5%"},
	];

	function descendingComparator(a, b, orderBy) {
		if (b[orderBy] < a[orderBy]) return -1;
		if (b[orderBy] > a[orderBy]) return 1;
		return 0;
	}

	function getComparator(order, orderBy) {
		return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
	}

	function stableSort(array, comparator) {
		const stabilized = array.map((el, index) => [el, index]);
		stabilized.sort((a, b) => {
			const order = comparator(a[0], b[0]);
			if (order !== 0) return order;
			return a[1] - b[1];
		});
		return stabilized.map((el) => el[0]);
	}

	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	useEffect(() => {
		setLoading(true);
		AddressServices({page, pageSize, order, orderBy, ...addressFilter})
			.then((res) => {
				setLoading(false);
				if (res?.status) {
					setList(res?.data?.list || []);
					setTotalRecode(res?.data?.pagination?.total || 0);
				} else {
					setList([]);
					setTotalRecode(0);
				}
			})
			.catch(() => {
				setLoading(false);
				setList([]);
				setTotalRecode(0);
			});
	}, [page, pageSize, order, orderBy, addressFilter]);

	useEffect(() => {
		setPage(1);
	}, [addressFilter]);

	return (
		<Paper sx={{width: "100%", overflow: "hidden"}}>
			<TableContainer>
				<Table aria-labelledby="ratings table" sx={{minWidth: 750}}>
					{/* Table Head */}
					<TableHead>
						<TableRow>
							{headCells.map((headCell) => (
								<TableCell key={headCell.id} sx={{fontWeight: 600, width: headCell.width}} sortDirection={orderBy === headCell.id ? order : false}>
									{headCell.label}
									{/* <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : "asc"} onClick={() => handleRequestSort(headCell.id)}>
										{orderBy === headCell.id ? (
											<Box component="span" sx={visuallyHidden}>
												{order === "desc" ? "sorted descending" : "sorted ascending"}
											</Box>
										) : null}
									</TableSortLabel> */}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={headCells.length}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											height: "400px",
										}}>
										<CustomLoader />
									</Box>
								</TableCell>
							</TableRow>
						) : (
							<>
								{stableSort(list, getComparator(order, orderBy)).map((row, index) => (
									<TableRow hover key={index} sx={{cursor: "pointer"}}>
										<TableCell>{truncateWords(row?.full_address, 40) || "-"}</TableCell>
										<TableCell>
											<CustomUserName user={row?.user} />
										</TableCell>
										<TableCell>
											<CustomRatingStar star={row?.aggregate_rating} />
										</TableCell>
										<TableCell>{getStatusBadge(row.manual_id)}</TableCell>
										<TableCell>{fDate(row?.createdAt)} </TableCell>
									</TableRow>
								))}

								{list?.length === 0 && (
									<TableRow>
										<TableCell colSpan={headCells.length}>
											<Typography align="center" sx={{py: 2}}>
												No records found
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{!loading && (
				<CustomPagination
					current={page}
					pageSize={pageSize}
					total={totalRecode}
					onChange={(e) => setPage(e)}
					onShowSizeChange={(p, e) => {
						setPage(p);
						setPageSize(e);
					}}
				/>
			)}
		</Paper>
	);
};

const ContractorReports = ({contractorFilter}) => {
	const [order, setOrder] = useState("asc");
	const [orderBy, setOrderBy] = useState("calories");
	const [list, setList] = useState([]);

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalRecode, setTotalRecode] = useState(0);
	const [loading, setLoading] = useState(true);

	const getStatusBadge = (status) => {
		if (status == 1) {
			return <Chip label="Verified" variant="" color="success" />;
		} else {
			return <Chip label="Not Verified" variant="" color="error" />;
		}
	};

	const headCells = [
		{id: "username", label: "Contractor Name", width: "23%"},
		{id: "email", label: "Contractor Email	", width: "23%"},
		{id: "business", label: "Business", width: "23%"},
		{id: "verified", label: "Verified", width: "15%"},
		{id: "created", label: "Created", width: "15%"},
	];

	function descendingComparator(a, b, orderBy) {
		if (b[orderBy] < a[orderBy]) return -1;
		if (b[orderBy] > a[orderBy]) return 1;
		return 0;
	}

	function getComparator(order, orderBy) {
		return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
	}

	function stableSort(array, comparator) {
		const stabilized = array.map((el, index) => [el, index]);
		stabilized.sort((a, b) => {
			const order = comparator(a[0], b[0]);
			if (order !== 0) return order;
			return a[1] - b[1];
		});
		return stabilized.map((el) => el[0]);
	}

	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	useEffect(() => {
		setLoading(true);
		ContractorServices({page, pageSize, order, orderBy, ...contractorFilter})
			.then((res) => {
				setLoading(false);
				if (res?.status) {
					setList(res?.data?.list || []);
					setTotalRecode(res?.data?.pagination?.total || 0);
				} else {
					setList([]);
					setTotalRecode(0);
				}
			})
			.catch(() => {
				setLoading(false);
				setList([]);
				setTotalRecode(0);
			});
	}, [page, pageSize, order, orderBy, contractorFilter]);

	useEffect(() => {
		setPage(1);
	}, [contractorFilter]);

	return (
		<Paper sx={{width: "100%", overflow: "hidden"}}>
			<TableContainer>
				<Table sx={{minWidth: 750}}>
					<TableHead>
						<TableRow>
							{headCells.map((headCell) => (
								<TableCell key={headCell.id} sx={{fontWeight: 600, width: headCell.width}} sortDirection={orderBy === headCell.id ? order : false}>
									{headCell.label}
									{/* <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : "asc"} onClick={() => handleRequestSort(headCell.id)}>
										{orderBy === headCell.id ? (
											<Box component="span" sx={visuallyHidden}>
												{order === "desc" ? "sorted descending" : "sorted ascending"}
											</Box>
										) : null}
									</TableSortLabel> */}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={headCells.length}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											height: "400px",
										}}>
										<CustomLoader />
									</Box>
								</TableCell>
							</TableRow>
						) : (
							<>
								{stableSort(list, getComparator(order, orderBy)).map((row, index) => (
									<TableRow hover key={index} sx={{cursor: "pointer"}}>
										<TableCell>
											<CustomUserName user={row} />
										</TableCell>
										<TableCell>{row?.email || "-"}</TableCell>
										<TableCell>{row.business_name}</TableCell>
										<TableCell>{getStatusBadge(row.verified)}</TableCell>
										<TableCell>{moment(row?.createdAt)?.format("MMM DD, YYYY") ?? "-"} </TableCell>
									</TableRow>
								))}

								{list.length === 0 && (
									<TableRow>
										<TableCell colSpan={headCells.length}>
											<Typography align="center" sx={{py: 2}}>
												No records found
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{!loading && (
				<CustomPagination
					current={page}
					pageSize={pageSize}
					total={totalRecode}
					onChange={(e) => setPage(e)}
					onShowSizeChange={(p, e) => {
						setPage(p);
						setPageSize(e);
					}}
				/>
			)}
		</Paper>
	);
};

export default Reports;
